// Lines, pills, cards and zones of open positions (moon-position-lines-spec.md,
// E6, E10–E12, E16). Sizes in CSS pixels.

/** What a line, pill or zone is painted like. */
export const POSITION_TONE = {
  ENTRY: "entry",
  PROFIT: "profit",
  LOSS: "loss",
};

/** Where a zone goes from its line; where the real price of a pinned line is. */
export const POSITION_DIRECTION = {
  UP: "up",
  DOWN: "down",
};

/** Badge text of a zero price (liquidation of a 1x long). */
export const ZERO_PRICE_TEXT = "0.00";

// Theme colours (theme.js) of each tone: `line` for pills and badges, `stroke` for the line itself
export const TONE_COLORS = {
  [POSITION_TONE.ENTRY]: { line: "positionEntry", stroke: "positionEntryLine", text: "positionEntryText" },
  [POSITION_TONE.PROFIT]: {
    line: "positionProfit",
    stroke: "positionProfit",
    text: "positionProfitText",
    zone: "positionProfitZone",
  },
  [POSITION_TONE.LOSS]: {
    line: "positionLoss",
    stroke: "positionLoss",
    text: "positionLossText",
    zone: "positionLossZone",
  },
};

// 1 CSS px: two device pixels on dense screens, one otherwise
export const POSITION_LINE_WIDTH_PX = 2;
// Pills stand this far from the left edge; the line starts this far right of the pill
// (or of its ▲ / ▼), so it does not touch it (moon.com)
export const POSITION_PILL_LEFT_PX = 14;
export const POSITION_LINE_GAP_PX = 6;
// The card stands on the entry line at this share of the pane width, as on moon.com a bit left of the middle
export const POSITION_CARD_X_RATIO = 0.36;
// Between cards and between price badges moved apart so they do not overlap (moon: 4)
export const POSITION_LABEL_GAP_PX = 4;
// Between a pinned pill and the bottom edge of the pane
export const POSITION_EDGE_GAP_PX = 2;
// ▲ / ▼ right of the pill of a pinned line, the line starts after it: pill, gap, ▼, same gap, line
export const POSITION_PIN_ARROW = { width: 7, height: 5, gap: 5 };
// ▲ under the bar the position was opened on
export const ENTRY_MARKER = { width: 8, height: 6, gap: 4 };
// Touch area of the close button (E16)
export const POSITION_CLOSE_TOUCH_SIZE_PX = 32;
export const POSITION_CLOSE_ICON_LINE_WIDTH_PX = 1.5;
export const POSITION_CARD_BORDER_WIDTH_PX = 1;
export const POSITION_FONT_WEIGHT = 500;
// Leverage and PnL of a card are semibold, as on moon.com
export const POSITION_CARD_FONT_WEIGHT = 600;

// Charts up to this width (phones) get the compact pills and no cards, text stays at least 10 px (E16)
const COMPACT_MAX_CHART_WIDTH_PX = 480;

// Measured on moon.com (CSS px): pill 20 high, 12 px text, 10 px padding;
// card 28 high, 13 px text, about 220 wide with four cells, close cell 28.
// The card is drawn 20 % smaller (22 high) at the user's wish, everything inside scaled with it
const REGULAR_SIZES = {
  pillFontSize: 12,
  pillHeight: 20,
  pillPaddingX: 10,
  cardFontSize: 11,
  cardHeight: 22,
  cardRadius: 5,
  cellPaddingX: 8,
  iconCellWidth: 26,
  iconSize: 11,
  // Leverage and PnL cells are at least this wide, so the card does not jump on every tick
  leverageCellMinWidth: 64,
  pnlCellMinWidth: 64,
  closeCellWidth: 22,
  closeIconSize: 7,
};

const COMPACT_SIZES = {
  pillFontSize: 11,
  pillHeight: 17,
  pillPaddingX: 8,
};

/**
 * @param {number} chartWidth - CSS px of the whole chart
 * @returns {object} sizes of pills and cards
 */
export const positionSizesFor = (chartWidth) =>
  chartWidth <= COMPACT_MAX_CHART_WIDTH_PX ? COMPACT_SIZES : REGULAR_SIZES;

/**
 * Phones show only the lines, pills and price badges, without cards.
 *
 * @param {number} chartWidth - CSS px of the whole chart
 * @returns {boolean}
 */
export const hasPositionCards = (chartWidth) => chartWidth > COMPACT_MAX_CHART_WIDTH_PX;

/**
 * @param {number} fontSize - CSS px
 * @param {string} fontFamily
 * @param {number} [fontWeight]
 * @returns {string} CSS font of pills and cards
 */
export const positionFont = (fontSize, fontFamily, fontWeight = POSITION_FONT_WEIGHT) =>
  `${fontWeight} ${fontSize}px ${fontFamily}`;
